import { getAdminToken } from "@/lib/adminToken";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  X,
  AlertCircle,
  CheckCircle2,
  Shield,
  UserCog,
  Eye,
  EyeOff,
  Mail,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

interface AdminUser {
  id: number;
  name: string;
  email: string;
  role: "super-admin" | "admin" | "sub-admin" | "editor";
  personal_email?: string;
  gmail_email?: string;
  mail_addresses?: Array<string | { email: string; name?: string }> | string;
  created_at: string;
}

function parseMailAddresses(value?: Array<string | { email: string; name?: string }> | string) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((item) => typeof item === "string" ? item : item.name ? `${item.name} <${item.email}>` : item.email);

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object" && "email" in item) {
        const mailbox = item as { email: string; name?: string };
        return mailbox.name ? `${mailbox.name} <${mailbox.email}>` : mailbox.email;
      }
      return "";
    }).filter(Boolean);
  } catch {
    // Older records may contain a comma-separated value instead of JSON.
  }

  return value.split(/[\n,;]+/).map((address) => address.trim()).filter(Boolean);
}

function getUserMailAddresses(user: AdminUser) {
  const addresses = [...parseMailAddresses(user.mail_addresses), ...(user.personal_email ? [user.personal_email] : [])];

  const seen = new Set<string>();
  const uniqueAddresses: string[] = [];
  addresses.forEach((address) => {
    const normalized = parseMailboxEntry(address).email.toLowerCase();
    if (!seen.has(normalized)) {
      seen.add(normalized);
      uniqueAddresses.push(address);
    }
  });
  return uniqueAddresses;
}

function parseMailboxEntry(value: string) {
  const match = value.trim().match(/^(.*?)\s*<([^<>]+)>$/);
  return match
    ? { name: match[1].trim(), email: match[2].trim() }
    : { name: "", email: value.trim() };
}

export default function AdminUsers() {
  const { user: currentUser } = useAdminAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    gmail_email: "",
    mail_addresses: "",
    password: "",
    role: "editor" as AdminUser["role"],
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewingUserId, setViewingUserId] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const token = getAdminToken();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setUsers(data);
      } else {
        setError(data.error || "Failed to load users");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openCreate = () => {
    setEditingUser(null);
    setFormData({ name: "", email: "", gmail_email: "", mail_addresses: "", password: "", role: "editor" });
    setModalOpen(true);
  };

  const openEdit = (u: AdminUser) => {
    setEditingUser(u);
    setFormData({
      name: u.name,
      email: u.email,
      gmail_email: u.gmail_email || "",
      mail_addresses: getUserMailAddresses(u).join(", "),
      password: "",
      role: u.role,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const url = editingUser
        ? `/api/admin/users/${editingUser.id}`
        : "/api/admin/users";
      const method = editingUser ? "PUT" : "POST";

      const mailboxEntries = parseMailAddresses(formData.mail_addresses).map(parseMailboxEntry).filter((mailbox) => mailbox.email);
      const body: Record<string, unknown> = {
        name: formData.name,
        email: formData.email,
        personal_email: mailboxEntries[0]?.email || "",
        gmail_email: formData.gmail_email,
        mail_addresses: mailboxEntries,
        role: formData.role,
      };
      if (formData.password) body.password = formData.password;

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (res.ok) {
        setSuccess(editingUser ? "User updated successfully" : "User created successfully");
        setModalOpen(false);
        fetchUsers();
      } else {
        setError(data.error || "Operation failed");
      }
    } catch {
      setError("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    setError("");
    setSuccess("");
    try {
      const res = await fetch(`/api/admin/users/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess("User deleted successfully");
        fetchUsers();
      } else {
        setError(data.error || "Delete failed");
      }
    } catch {
      setError("Network error");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleViewAccount = async (target: AdminUser) => {
    const viewTab = window.open("about:blank", "_blank");
    if (!viewTab) {
      setError("Allow pop-ups to open the user account in a new tab.");
      return;
    }
    viewTab.opener = null;
    setViewingUserId(target.id);
    setError("");

    try {
      const response = await fetch(`/api/admin/impersonate/${target.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getAdminToken()}` },
      });
      const data = await response.json();
      if (!response.ok || !data.token) {
        throw new Error(data.error || "Could not open this user account");
      }

      viewTab.location.href = `${window.location.origin}/admin/impersonate#${encodeURIComponent(data.token)}`;
    } catch (err) {
      viewTab.close();
      setError(err instanceof Error ? err.message : "Could not open this user account");
    } finally {
      setViewingUserId(null);
    }
  };

  const roleBadge = (role: string) => {
    const styles: Record<string, string> = {
      "super-admin": "bg-red-100 text-red-700",
      admin: "bg-blue-100 text-blue-700",
      "sub-admin": "bg-purple-100 text-purple-700",
      editor: "bg-green-100 text-green-700",
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full capitalize font-medium ${styles[role] || "bg-neutral-100"}`}>
        {role.replace("-", " ")}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-8 h-0.5 bg-accent rounded-full" />
            <span className="text-accent text-xs font-medium uppercase tracking-wide">Management</span>
          </div>
          <h1 className="text-2xl font-bold text-primary">Admin Users</h1>
          <p className="text-neutral-400 text-sm mt-1">Manage admin accounts and access levels</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent/90 text-white rounded-xl text-sm font-medium transition-colors shadow-lg shadow-accent/20"
        >
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-red-600 text-sm">{error}</span>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="bg-green-50 border border-green-100 rounded-xl p-4 flex items-center gap-3"
          >
            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span className="text-green-600 text-sm">{success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-neutral-100 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-neutral-400 text-sm">No users found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-100 bg-neutral-50/50">
                  <th className="text-left px-5 py-3.5 font-medium text-neutral-500">Name</th>
                  <th className="text-left px-5 py-3.5 font-medium text-neutral-500">Mailbox Addresses</th>
                  <th className="text-left px-5 py-3.5 font-medium text-neutral-500">Linked Gmail</th>
                  <th className="text-left px-5 py-3.5 font-medium text-neutral-500">Role</th>
                  <th className="text-right px-5 py-3.5 font-medium text-neutral-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-neutral-50 hover:bg-neutral-50/30 transition-colors">
                    <td className="px-5 py-4 font-medium text-primary">{u.name}</td>
                    <td className="px-5 py-4 text-neutral-600 max-w-xs break-words">
                      {getUserMailAddresses(u).length ? (
                        <div className="space-y-1">
                          {getUserMailAddresses(u).map((address, index) => (
                            <div key={address} className="flex items-center gap-2">
                              <span>{address}</span>
                              {index === 0 && <span aria-label="Default mailbox" title="Default mailbox" className="inline-block w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />}
                            </div>
                          ))}
                        </div>
                      ) : <span className="text-xs text-neutral-300">Not set</span>}
                    </td>
                    <td className="px-5 py-4 text-neutral-600">
                      {u.gmail_email ? (
                        <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-neutral-400" />{u.gmail_email}</span>
                      ) : <span className="text-xs text-neutral-300">Not set</span>}
                    </td>
                    <td className="px-5 py-4">{roleBadge(u.role)}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => void handleViewAccount(u)}
                          className="p-2 hover:bg-amber-50 text-neutral-400 hover:text-amber-600 rounded-lg transition-colors disabled:opacity-50"
                          title={`View ${u.name}'s account in a new tab`}
                          aria-label={`View ${u.name}'s account in a new tab`}
                          disabled={u.id === currentUser?.id || viewingUserId !== null}
                        >
                          {viewingUserId === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => openEdit(u)}
                          className="p-2 hover:bg-blue-50 text-neutral-400 hover:text-blue-600 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(u.id)}
                          className="p-2 hover:bg-red-50 text-neutral-400 hover:text-red-600 rounded-lg transition-colors"
                          title="Delete"
                          disabled={u.id === currentUser?.id}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-primary">
                  {editingUser ? "Edit User" : "Add New User"}
                </h3>
                <button onClick={closeModal} className="p-2 hover:bg-neutral-100 rounded-lg transition-colors">
                  <X className="w-4 h-4 text-neutral-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    placeholder="john@geoenz.com"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                    Mailbox Addresses (one per line)
                  </label>
                  <textarea
                    value={formData.mail_addresses}
                    onChange={(e) => {
                      setFormData((previous) => ({ ...previous, mail_addresses: e.target.value }));
                    }}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    placeholder={"GeoENZ Admin <admin@geoenz.com>\nInformation <info@geoenz.com>\nSupport <support@geoenz.com>"}
                  />
                  <p className="text-xs text-neutral-400 mt-1">Enter one mailbox per line as Display Name &lt;address&gt;. The first mailbox is the default sender; names appear on outgoing mail.</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                    Linked Gmail
                  </label>
                  <input
                    type="email"
                    value={formData.gmail_email}
                    onChange={(e) => setFormData({ ...formData, gmail_email: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    placeholder="samuel@gmail.com"
                  />
                  <p className="text-xs text-neutral-400 mt-1">Receives forwarding notifications for new inbound mail.</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as AdminUser["role"] })}
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm bg-white"
                  >
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                    <option value="sub-admin">Sub-admin</option>
                    <option value="super-admin">Super Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                    {editingUser ? "New Password (leave blank to keep current)" : "Password"}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingUser}
                      className="w-full px-4 py-2.5 pr-10 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                      placeholder={editingUser ? "••••••••" : "Enter password"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 rounded-xl border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2.5 rounded-xl bg-accent hover:bg-accent/90 text-white transition-colors text-sm font-medium disabled:opacity-70"
                  >
                    {submitting ? "Saving..." : editingUser ? "Update User" : "Create User"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary">Delete User</h3>
                  <p className="text-sm text-neutral-500">This action cannot be undone.</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2.5 rounded-xl border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white transition-colors text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
